// frontend/src/api/messagingApi.js
// Messaging API calls for the platform messaging system
// Implementation follows patterns from platformApi.js

import { API_BASE, getCsrfHeaders } from './transport';

/**
 * Get user's inbox messages
 * @param {Object} options - Query options
 * @param {boolean} options.includeBroadcasts - Include system broadcasts (default: true)
 * @param {string} options.status - Filter by status: 'UNREAD', 'READ', 'ARCHIVED' (optional)
 * @param {number} options.limit - Max messages to return (default: 50)
 * @returns {Promise<{messages: Array, unread_count: number, total_count: number}>}
 */
export function getInbox({ includeBroadcasts = true, status = null, limit = 50 } = {}) {
    console.log(`messagingApi: Getting inbox (includeBroadcasts: ${includeBroadcasts}, status: ${status || 'all'}, limit: ${limit})`);

    const params = new URLSearchParams();
    params.append('include_broadcasts', includeBroadcasts);
    if (status) params.append('status', status);
    params.append('limit', limit);

    return fetch(`${API_BASE}/platform/messages/inbox/?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`messagingApi: Get inbox response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Get inbox failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get inbox`);
            }).catch(err => {
                if (err.message.includes('Failed to get inbox')) throw err;
                throw new Error(`HTTP ${response.status}: Failed to get inbox`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Inbox retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.messages)) {
            throw new Error('❌ INVALID RESPONSE: Expected "messages" array in inbox response');
        }
        if (typeof data.unread_count !== 'number') {
            throw new Error('❌ INVALID RESPONSE: Expected "unread_count" number in inbox response');
        }
        if (typeof data.total_count !== 'number') {
            throw new Error('❌ INVALID RESPONSE: Expected "total_count" number in inbox response');
        }

        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error getting inbox:', error);
        throw error;
    });
}

/**
 * Send a canned P2P message to a friend
 * @param {number} toUserId - Recipient's user ID
 * @param {string} subtype - One of: 'GIF', 'WITTY', 'BATTLE_RESULT'
 * @param {Object} battleData - Battle result data (required for BATTLE_RESULT subtype)
 * @returns {Promise<{message: string, message_id: number, sent_to: Object}>}
 */
export function sendCannedMessage(toUserId, subtype, battleData = null) {
    console.log(`messagingApi: Sending canned message (to: ${toUserId}, subtype: ${subtype})`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!toUserId || typeof toUserId !== 'number') {
        throw new Error(`❌ sendCannedMessage: Invalid toUserId "${toUserId}" - must be a number`);
    }
    if (!subtype || !['GIF', 'WITTY', 'BATTLE_RESULT'].includes(subtype)) {
        throw new Error(`❌ sendCannedMessage: Invalid subtype "${subtype}" - must be GIF, WITTY, or BATTLE_RESULT`);
    }
    if (subtype === 'BATTLE_RESULT' && !battleData) {
        throw new Error('❌ sendCannedMessage: battleData is required for BATTLE_RESULT messages');
    }

    const body = {
        to_user_id: toUserId,
        subtype: subtype
    };
    if (battleData) {
        body.battle_data = battleData;
    }

    return fetch(`${API_BASE}/platform/messages/send-canned/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(body)
    })
    .then(response => {
        console.log(`messagingApi: Send canned message response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Send canned message failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to send message`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Canned message sent successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error sending canned message:', error);
        throw error;
    });
}

/**
 * Get user's recent completed battles for sharing
 * @param {number} limit - Max battles to return (default: 10)
 * @returns {Promise<{battles: Array, count: number}>}
 */
export function getRecentBattles(limit = 10) {
    console.log(`messagingApi: Getting recent battles (limit: ${limit})`);

    return fetch(`${API_BASE}/platform/messages/recent-battles/?limit=${limit}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`messagingApi: Get recent battles response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Get recent battles failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get recent battles`);
            }).catch(err => {
                if (err.message.includes('Failed to get recent battles')) throw err;
                throw new Error(`HTTP ${response.status}: Failed to get recent battles`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Recent battles retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.battles)) {
            throw new Error('❌ INVALID RESPONSE: Expected "battles" array in response');
        }

        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error getting recent battles:', error);
        throw error;
    });
}

/**
 * Submit a bug report to the system
 * @param {string} description - Bug description (min 10 chars, max 2000)
 * @param {string} userAgent - Browser user agent (optional)
 * @param {string} pageUrl - Current page URL (optional)
 * @returns {Promise<{message: string, report_id: number, thank_you_message: string}>}
 */
export function submitBugReport(description, userAgent = '', pageUrl = '') {
    console.log(`messagingApi: Submitting bug report`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!description || typeof description !== 'string') {
        throw new Error('❌ submitBugReport: description must be a non-empty string');
    }
    if (description.length < 10) {
        throw new Error('❌ submitBugReport: description must be at least 10 characters');
    }
    if (description.length > 2000) {
        throw new Error('❌ submitBugReport: description must be 2000 characters or less');
    }

    return fetch(`${API_BASE}/platform/messages/bug-report/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            description: description,
            user_agent: userAgent,
            page_url: pageUrl
        })
    })
    .then(response => {
        console.log(`messagingApi: Submit bug report response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Submit bug report failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to submit bug report`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Bug report submitted successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error submitting bug report:', error);
        throw error;
    });
}

/**
 * Mark a message as read
 * @param {number} messageId - Message ID to mark as read
 * @returns {Promise<{message: string, message_id: number}>}
 */
export function markMessageAsRead(messageId) {
    console.log(`messagingApi: Marking message ${messageId} as read`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!messageId || typeof messageId !== 'number') {
        throw new Error(`❌ markMessageAsRead: Invalid messageId "${messageId}" - must be a number`);
    }

    return fetch(`${API_BASE}/platform/messages/${messageId}/read/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`messagingApi: Mark message as read response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Mark message as read failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to mark message as read`);
            }).catch(err => {
                if (err.message.includes('Failed to mark message as read')) throw err;
                throw new Error(`HTTP ${response.status}: Failed to mark message as read`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Message marked as read:`, data);
        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error marking message as read:', error);
        throw error;
    });
}

/**
 * Delete a message (soft delete)
 * @param {number} messageId - Message ID to delete
 * @returns {Promise<{message: string, message_id: number}>}
 */
export function deleteMessage(messageId) {
    console.log(`messagingApi: Deleting message ${messageId}`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!messageId || typeof messageId !== 'number') {
        throw new Error(`❌ deleteMessage: Invalid messageId "${messageId}" - must be a number`);
    }

    return fetch(`${API_BASE}/platform/messages/${messageId}/`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`messagingApi: Delete message response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`messagingApi: Delete message failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to delete message`);
            }).catch(err => {
                if (err.message.includes('Failed to delete message')) throw err;
                throw new Error(`HTTP ${response.status}: Failed to delete message`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`messagingApi: Message deleted:`, data);
        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error deleting message:', error);
        throw error;
    });
}

/**
 * Get count of unread messages
 * @returns {Promise<{unread_count: number}>}
 */
export function getUnreadCount() {
    return fetch(`${API_BASE}/platform/messages/unread-count/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to get unread count`);
        }
        return response.json();
    })
    .then(data => {
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (typeof data.unread_count !== 'number') {
            throw new Error('❌ INVALID RESPONSE: Expected "unread_count" number in response');
        }
        return data;
    })
    .catch(error => {
        console.error('messagingApi: Error getting unread count:', error);
        throw error;
    });
}
